import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bug, MessageSquare, Heart, Loader2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useUserFeedback } from '@/hooks/useFeedback';
import { FeedbackDialog } from './FeedbackDialog';

const typeIcons = {
  bug: Bug,
  feedback: MessageSquare,
  praise: Heart,
};

const typeKeys = {
  bug: 'bug',
  feedback: 'feedback',
  praise: 'praise',
};

const statusStyles: Record<string, string> = {
  open: 'bg-info-light text-info-ink',
  acknowledged: 'bg-warning-light text-warning-ink',
  resolved: 'bg-success-light text-success-ink',
};

export function FeedbackList() {
  const { t, i18n } = useTranslation('roadmap');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'bug' | 'feedback' | 'praise'>('feedback');
  const { data: feedbacks, isLoading } = useUserFeedback();

  const openDialog = (type: 'bug' | 'feedback' | 'praise') => {
    setDialogType(type);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => openDialog('bug')}>
          <Bug className="h-4 w-4 mr-1" />
          {t('feedback.reportBug')}
        </Button>
        <Button size="sm" variant="outline" onClick={() => openDialog('feedback')}>
          <MessageSquare className="h-4 w-4 mr-1" />
          {t('feedback.giveFeedback')}
        </Button>
        <Button size="sm" variant="outline" onClick={() => openDialog('praise')}>
          <Heart className="h-4 w-4 mr-1" />
          {t('feedback.sharePraise')}
        </Button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !feedbacks?.length ? (
        <div className="text-center py-12 text-muted-foreground">
          <p className="text-sm">{t('feedback.empty')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {feedbacks.map((fb) => {
            const Icon = typeIcons[fb.type] || MessageSquare;
            return (
              <Card key={fb.id}>
                <CardContent className="flex items-start gap-3 p-4">
                  <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-sm">{fb.title}</h3>
                    </div>
                    {fb.description && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                        {fb.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {t(`feedback.types.${typeKeys[fb.type]}`)}
                      </Badge>
                      <span className={`inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-medium ${statusStyles[fb.status] || ''}`}>
                        {t(`feedback.statuses.${fb.status}`)}
                      </span>
                      {fb.severity && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {t(`feedbackDialog.severities.${fb.severity}`)}
                        </Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {new Date(fb.created_at).toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US')}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <FeedbackDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        defaultType={dialogType}
      />
    </div>
  );
}
